import React, { useEffect, useState } from 'react';
import { RefreshCw, Eye, Send, FileSignature } from 'lucide-react';
import { Dialog } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import AgreementComposeDialog, { DEFAULT_AGREEMENT_SUBJECT, DEFAULT_AGREEMENT_BODY } from './AgreementComposeDialog';
import { friendlyUploadError } from '../lib/errors';
import { fmtDate } from '../lib/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL;

const AGR_STATUS = {
  sent: { cls: 'bg-sky-100 text-sky-800', label: 'Awaiting signature' },
  signed: { cls: 'bg-emerald-100 text-emerald-800', label: 'Signed' },
  superseded: { cls: 'bg-gray-100 text-gray-600', label: 'Superseded' },
  voided: { cls: 'bg-gray-100 text-gray-600', label: 'Voided' },
  declined: { cls: 'bg-rose-100 text-rose-700', label: 'Declined' },
};

export default function AgreementsLog() {
  const [rows, setRows] = useState(null);
  const [resendFor, setResendFor] = useState(null);
  const [form, setForm] = useState({
    email_subject: DEFAULT_AGREEMENT_SUBJECT,
    email_body: DEFAULT_AGREEMENT_BODY,
    agreement_pdf: null,
  });
  const [sending, setSending] = useState(false);

  async function load() {
    try {
      const res = await fetch(`${API}/api/admin/agreements`, { credentials: 'include' });
      if (res.ok) setRows((await res.json()).agreements || []);
      else setRows([]);
    } catch { setRows([]); }
  }
  useEffect(() => { load(); }, []);

  async function handleResend(e) {
    e.preventDefault();
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('agreement_pdf', form.agreement_pdf);
      fd.append('email_subject', form.email_subject);
      fd.append('email_body', form.email_body || '');
      const res = await fetch(`${API}/api/admin/recruiters/${resendFor.recruiter_id}/send-agreement`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed');
      setResendFor(null);
      setForm((f) => ({ ...f, agreement_pdf: null }));
      load();
    } catch (err) {
      alert(friendlyUploadError(err, 'Failed to send agreement.'));
    } finally {
      setSending(false);
    }
  }

  const list = rows || [];
  const counts = {
    total: list.length,
    awaiting: list.filter((r) => r.status === 'sent').length,
    signed: list.filter((r) => r.status === 'signed').length,
  };

  return (
    <div className="space-y-6" data-testid="agreements-log">
      <div className="grid grid-cols-3 gap-4">
        {[
          { l: 'Agreements sent', v: counts.total, c: 'text-tge-navy' },
          { l: 'Awaiting signature', v: counts.awaiting, c: 'text-sky-600' },
          { l: 'Signed', v: counts.signed, c: 'text-emerald-600' },
        ].map((s) => (
          <div key={s.l} className="bg-white rounded-2xl p-5 border border-tge-navy/5">
            <div className="text-[11px] uppercase tracking-wider text-tge-muted">{s.l}</div>
            <div className={`font-serif-display text-3xl mt-2 ${s.c}`}>{s.v}</div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-tge-navy/5 overflow-hidden">
        <div className="px-6 py-5 border-b border-tge-navy/5 flex items-center justify-between">
          <div>
            <h3 className="font-serif-display text-xl text-tge-navy">Agreement history</h3>
            <p className="text-xs text-tge-muted mt-1">Every partnership agreement sent via DocuSign — who sent it, when, and its signature status.</p>
          </div>
          <button
            onClick={load}
            className="inline-flex items-center gap-1.5 text-xs text-tge-muted hover:text-tge-navy transition px-3 py-1.5 rounded-full border border-tge-navy/10 hover:border-tge-navy/30 hover:bg-tge-cream-soft"
            data-testid="agreements-refresh-btn"
          >
            <RefreshCw size={12} /> Refresh
          </button>
        </div>

        {rows === null ? (
          <div className="text-center py-12 text-tge-muted text-sm">Loading…</div>
        ) : list.length === 0 ? (
          <div className="text-center py-16 text-tge-muted text-sm">
            <FileSignature className="mx-auto text-tge-cream" size={36} />
            <p className="mt-3">No agreements sent yet. Send one from the Recruiters tab.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-tge-cream-soft">
                <tr>
                  {['Recruiter', 'Sent', 'Subject', 'Status', 'Signed', 'Actions'].map((h) => (
                    <th key={h} className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-tge-muted">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-tge-cream">
                {list.map((r) => {
                  const st = AGR_STATUS[r.status] || { cls: 'bg-gray-100 text-gray-600', label: r.status || 'unknown' };
                  return (
                    <tr key={`${r.recruiter_id}-${r.envelope_id}`} className="hover:bg-tge-cream-soft/50 transition" data-testid={`agreement-row-${r.envelope_id}`}>
                      <td className="px-6 py-4">
                        <div className="font-medium text-tge-navy text-sm">{r.company_name || '—'}</div>
                        <div className="text-xs text-tge-muted">{r.recipient_email || r.contact_email || '—'}</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-tge-navy">
                        <div>{r.sent_at ? fmtDate(r.sent_at) : '—'}</div>
                        {r.sent_by?.name && <div className="text-[11px] text-tge-muted">by {r.sent_by.name}</div>}
                        {r.is_resend && <span className="text-[10px] uppercase tracking-wider text-tge-gold font-semibold">Resend</span>}
                      </td>
                      <td className="px-6 py-4 text-sm text-tge-muted max-w-[240px] truncate" title={r.subject}>{r.subject || '—'}</td>
                      <td className="px-6 py-4">
                        <Badge className={`${st.cls} border-0`}>{st.label}</Badge>
                      </td>
                      <td className="px-6 py-4 text-sm text-tge-muted">{r.signed_at ? fmtDate(r.signed_at) : '—'}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          {r.pdf_available && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-xs gap-1"
                              onClick={() => window.open(`${API}/api/admin/recruiters/${r.recruiter_id}/agreement`, '_blank')}
                              data-testid={`agreement-view-pdf-${r.envelope_id}`}
                            >
                              <Eye size={12} /> View PDF
                            </Button>
                          )}
                          {r.is_current && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-xs gap-1 border-tge-gold/50 text-tge-navy hover:bg-tge-cream-soft"
                              onClick={() => setResendFor(r)}
                              data-testid={`agreements-resend-btn-${r.recruiter_id}`}
                            >
                              <Send size={12} /> Resend
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={!!resendFor} onOpenChange={(o) => !o && setResendFor(null)}>
        {resendFor && (
          <AgreementComposeDialog
            recruiter={{
              company_name: resendFor.company_name,
              contact_name: resendFor.contact_name,
              contact_email: resendFor.contact_email || resendFor.recipient_email,
            }}
            form={form}
            setForm={setForm}
            sending={sending}
            onSubmit={handleResend}
            onClose={() => setResendFor(null)}
          />
        )}
      </Dialog>
    </div>
  );
}
